import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Archive, Delete, LayersFront } from '@factorialco/f0-react/icons/app'
import {
  EngagementCreateSurveyDocument,
  EngagementSurveysDataCollectionDocument,
  EngagementSurveysListPageDocument,
  FormsEnumsStatusEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivities } from 'modules/core/lib/activities/provider'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { formRecurrenceMapper } from 'modules/engagement_surveys/lib/formRecurrenceMapper'
import { formStatusMapper } from 'modules/engagement_surveys/lib/formStatusMapper'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const DEFAULT_SURVEY_STATUS_FILTERS: FormsEnumsStatusEnum[] = [
  'active',
  'draft',
  'scheduled',
  'finished',
]

export default function SurveysListPage() {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { openActivity } = useActivities()
  const { data: pageData, loading, error } = useQuery(EngagementSurveysListPageDocument)
  const { company } = useCurrentSession()

  const canCreateSurvey = pageData?.surveys._policies.surveysCreateSurvey.can ?? false

  const [createSurvey] = useMutation(EngagementCreateSurveyDocument, {
    extractorFn: (data) => data.surveysMutations.createSurveysSurvey,
  })

  const handleCreateSurvey = async () => {
    if (!company?.id) return

    const result = await createSurvey({
      variables: {
        companyId: company.id,
        title: i18n.t('surveys.default_title'),
      },
    })

    const handled = handleResult(result, () => null)

    if (handled.success && handled.data?.survey) {
      navigate(
        navigation.engagement.smart_surveys.survey.fullPath({
          surveyId: handled.data.survey.id,
        })
      )
    }
  }

  const source = useGraphqlDataCollectionSource({
    document: EngagementSurveysDataCollectionDocument,
    mapData: (data) => {
      return data.surveys.surveysConnection.nodes
    },
    mapPagination: (data) => ({
      total: Number(data.surveys.surveysConnection.totalCount),
    }),
    mapVariables: ({ filters, search }) => ({
      status:
        filters.status && filters.status.length > 0
          ? filters.status.filter(Boolean)
          : DEFAULT_SURVEY_STATUS_FILTERS,
      search,
    }),
    search: {
      enabled: true,
    },
    filters: {
      status: {
        label: i18n.t('surveys.surveys_table.filters.status'),
        type: 'in',
        options: {
          options: [
            { label: i18n.t('surveys.status.draft'), value: 'draft' },
            { label: i18n.t('surveys.status.scheduled'), value: 'scheduled' },
            { label: i18n.t('surveys.status.active'), value: 'active' },
            { label: i18n.t('surveys.status.finished'), value: 'finished' },
            { label: i18n.t('surveys.status.archived'), value: 'archived' },
          ],
        },
      },
    },
    itemUrl(item) {
      return navigation.engagement.smart_surveys.survey.fullPath({
        surveyId: item.id,
      })
    },
    primaryActions: () => {
      if (canCreateSurvey) {
        return {
          label: i18n.t('surveys.surveys_table.actions.create'),
          icon: Add,
          onClick: () => {
            handleCreateSurvey()
          },
        }
      }

      return undefined
    },
    itemActions: ({ id, form }) => {
      const actions = []

      if (form.status === 'finished') {
        actions.push({
          label: i18n.t('common.archive'),
          icon: Archive,
          onClick: () => {
            openActivity('engagement_surveys.archiveSurvey', { surveyId: id.toString() })
          },
        })

        actions.push({ type: 'separator' as const })
      }

      if (canCreateSurvey) {
        actions.push({
          label: i18n.t('common.duplicate'),
          icon: LayersFront,
          onClick: () => {
            openActivity('engagement_surveys.duplicateSurvey', { surveyId: id.toString() })
          },
        })
      }

      if (form.status === 'draft') {
        actions.push({
          critical: true,
          label: i18n.t('common.delete'),
          icon: Delete,
          onClick: () => {
            openActivity('engagement_surveys.deleteSurvey', { surveyId: id.toString() })
          },
        })
      }

      return actions
    },
  })

  return (
    <ResponsivePage.Body variant="standard" loading={loading} error={error}>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('surveys.surveys_table.columns.title'),
                  render: (item) => {
                    return item.form.title
                  },
                },
                {
                  label: i18n.t('surveys.surveys_table.columns.status'),
                  render: (item) => ({
                    type: 'status',
                    value: formStatusMapper(item.form.status),
                  }),
                },
                {
                  label: i18n.t('surveys.surveys_table.columns.recurrence'),
                  render: (item) => {
                    return formRecurrenceMapper(item.form.schedule?.recurrenceType || 'onetime')
                  },
                },
                {
                  label: i18n.t('surveys.surveys_table.columns.participation'),
                  render: (item) => {
                    if (['scheduled', 'draft'].includes(item.form.status)) {
                      return '-'
                    }

                    const questions = Number(item.form.sentCount)
                    const answers = Number(item.form.answeredCount)
                    const percentage = Number(((answers / questions) * 100).toFixed(1))

                    return {
                      type: 'percentage',
                      value: {
                        label: `${answers}/${questions}`,
                        percentage,
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
        emptyStates={{
          'no-data': {
            title: i18n.t('surveys.surveys_table.empty_states.no_data.title'),
            description: i18n.t('surveys.surveys_table.empty_states.no_data.description'),
            actions: canCreateSurvey
              ? [
                  {
                    label: i18n.t('surveys.surveys_table.empty_states.no_data.create_action'),
                    onClick: () => {
                      handleCreateSurvey()
                    },
                  },
                ]
              : undefined,
          },
        }}
      />
    </ResponsivePage.Body>
  )
}
